// Utility service to convert Hebrew Psalm references to AELF (Catholic/Greek) references
// Uses the mapping table from hebrew-aelf.json

import hebrewAelfMapping from '../assets/bible/hebrew-aelf.json';
import { getBookDisplayName } from '../utils/bible-displays';
// If you still get errors, try:
// import * as hebrewAelfMapping from '../assets/bible/hebrew-aelf.json';
// or require if using CommonJS: const hebrewAelfMapping = require('../assets/bible/hebrew-aelf.json');
// Make sure tsconfig.json has "resolveJsonModule": true and "esModuleInterop": true

// Add index signatures for dynamic access
interface HebrewToAelfMap {
  [key: string]: string | string[];
}
interface AelfToHebrewMap {
  [key: string]: string;
}
interface SplitPsalmMap {
  [key: string]: SplitPsalmInfo;
}

interface SplitPsalmInfo {
  parts: string[];
  aelf_mapping: string[];
  verse_split: number;
}

/**
 * Convert a Hebrew Psalm chapter reference to AELF chapter reference (for display only).
 * @param hebrewPsalm - The Hebrew Psalm number as string or number (e.g. '116', 116)
 * @returns The corresponding AELF psalm number (string, e.g. '9A', '114-115') or null if not found
 */
export function convertHebrewPsalmToAelf(hebrewPsalm: string | number): string | null {
  const mapping = hebrewAelfMapping['hebrew_to_aelf'] as HebrewToAelfMap;

  // Always use the mapping table for conversion
  const mapped = mapping[hebrewPsalm.toString()];
  if (Array.isArray(mapped)) {
    return `${mapped[0]}-${mapped[1]}`;
  }
  if (typeof mapped === 'string') {
    return mapped;
  }
  return null;
}

/**
 * Convert a Hebrew Psalm reference with verse range to AELF reference(s) with verse ranges.
 * @param hebrewPsalm - The Hebrew Psalm number as string or number
 * @param verseStart - Start verse (inclusive)
 * @param verseEnd - End verse (inclusive)
 * @returns Array of objects with aelfPsalm and verse range
 */
export function convertHebrewPsalmRangeToAelf(
  hebrewPsalm: string | number,
  verseStart: number,
  verseEnd: number
): Array<{ aelfPsalm: string; verseStart: number; verseEnd: number }> | null {
  const splitInfo = (hebrewAelfMapping['split_psalms']?.hebrew as SplitPsalmMap | undefined)?.[hebrewPsalm.toString()];
  if (!splitInfo) {
    // Not a split psalm, map directly
    const aelfPsalm = convertHebrewPsalmToAelf(hebrewPsalm);
    if (typeof aelfPsalm === 'string') {
      return [{ aelfPsalm, verseStart, verseEnd }];
    }
    // Should not happen, but handle gracefully
    return null;
  }
  // Handle split psalms
  const { aelf_mapping, verse_split } = splitInfo;
  const result: Array<{ aelfPsalm: string; verseStart: number; verseEnd: number }> = [];
  if (verseStart <= verse_split) {
    result.push({ aelfPsalm: aelf_mapping[0], verseStart, verseEnd: Math.min(verseEnd, verse_split) });
  }
  if (verseEnd > verse_split) {
    result.push({ aelfPsalm: aelf_mapping[1], verseStart: Math.max(verseStart, verse_split + 1), verseEnd });
  }
  return result;
}

/**
 * Convert an AELF Psalm reference to Hebrew reference(s).
 * @param aelfPsalm - The AELF Psalm number as string or number
 * @returns The corresponding Hebrew psalm number(s) or null if not found
 */
export function convertAelfPsalmToHebrew(aelfPsalm: string | number): string | null {
  const mapping = hebrewAelfMapping['aelf_to_hebrew'] as AelfToHebrewMap;
  const mapped = mapping[aelfPsalm.toString()];
  if (typeof mapped === 'string') {
    return mapped;
  }
  return null;
}

/**
 * Returns a display string for a Psalm chapter, e.g. 'Psaumes 10 (9B)' or 'Psaumes 116 (114-115)'.
 * Uses the localized book name for Psalms, and only adds the (aelf number) for Psalms 9 to 147 (inclusive).
 * @param hebrewPsalm - The Hebrew Psalm number as string or number
 * @param locale - The locale/language code (e.g. 'fr', 'en')
 * @returns Display string for the Psalm chapter
 */
export function getPsalmDisplayName(hebrewPsalm: string | number, locale: string): string {
  // Use the display name for Psalms in the given locale
  const bookName = getBookDisplayName('Ps', locale);
  const num = typeof hebrewPsalm === 'string' ? parseInt(hebrewPsalm, 10) : hebrewPsalm;
  const aelf = convertHebrewPsalmToAelf(hebrewPsalm);
  // Only show (aelf) if mapping exists and is different from the Hebrew number
  if (num >= 9 && num <= 147 && aelf && aelf !== String(num)) {
    return `${bookName} ${num} (${aelf})`;
  }
  return `${bookName} ${num}`;
}

// You can add more helpers as needed for your use case.
